<template>
  <v-card :class="embedded ? 'embedded-card' : 'bento-card third-card mt-4'" :elevation="embedded ? 0 : 2">
    <v-card-text class="card-content">
      <div class="d-flex align-center justify-space-between mb-2 header-row">
        <span class="section-title">Contratos</span>
        <v-btn
          v-if="canManageAccess"
          small
          color="primary"
          depressed
          :disabled="loading"
          @click="openAssociate"
        >
          <v-icon left small>mdi-plus</v-icon>Añadir
        </v-btn>
      </div>

      <div v-if="loading" class="d-flex justify-center py-6">
        <v-progress-circular indeterminate color="primary" size="32" />
      </div>

      <div v-else-if="!accesses.length" class="empty-state text-center py-4 grey--text">
        Sin contratos asociados.
      </div>

      <div v-else class="table-scroll">
        <v-simple-table dense>
          <template v-slot:default>
            <thead>
              <tr>
                <th scope="col" class="text-left">Compañía</th>
                <th scope="col" class="text-left">Contrato</th>
                <th scope="col" class="text-center">Cuenta</th>
                <th scope="col" class="text-center">Membresías contratadas</th>
                <th scope="col" class="text-center">Membresías activas</th>
                <th scope="col" class="text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in accesses" :key="item.id">
                <td>{{ item.companyName || '-' }}</td>
                <td>
                  {{ item.contratoName || '-' }}
                  <span class="caption d-block grey--text">{{ item.planName || 'Todos los planes' }}</span>
                </td>
                <td class="text-center">
                  <span :class="['accounts-badge', item.totalAccounts > 0 ? 'accounts-badge--active' : 'accounts-badge--inactive']">
                    {{ item.totalAccounts }}
                  </span>
                </td>
                <td class="text-center">{{ formatTotal(item) }}</td>
                <td class="text-center">{{ item.activeMemberships }}</td>
                <td class="text-center">
                <v-menu v-if="canManageAccess" offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small v-bind="attrs" v-on="on" :disabled="actionLoading">
                      <v-icon small>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item :disabled="!item.contratoId" @click="item.contratoId && openChangePlan(item)">
                      <v-list-item-icon class="mr-2"><v-icon small>mdi-swap-horizontal</v-icon></v-list-item-icon>
                      <v-list-item-title>Cambiar plan</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="openAdjust(item)">
                      <v-list-item-icon class="mr-2"><v-icon small>mdi-counter</v-icon></v-list-item-icon>
                      <v-list-item-title>Ajustar cuentas</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="toggleStatus(item)">
                      <v-list-item-icon class="mr-2">
                        <v-icon small>{{ item.status === 'active' ? 'mdi-pause-circle' : 'mdi-play-circle' }}</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>
                        {{ item.status === 'active' ? 'Desactivar' : 'Reactivar' }}
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item :disabled="!item.canDelete" @click="item.canDelete && confirmDelete(item)">
                      <v-list-item-icon class="mr-2"><v-icon small :color="item.canDelete ? 'error' : 'grey'">mdi-delete</v-icon></v-list-item-icon>
                      <v-list-item-title :class="{ 'grey--text': !item.canDelete }">
                        Eliminar
                        <span v-if="!item.canDelete" class="caption d-block grey--text">Tiene membresías activas</span>
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
    </v-card-text>

    <AssociateCompanyModal
      v-model="showAssociate"
      :user-id="userId"
      @saved="onAssociated"
      @error="showErrorSnackbar"
    />

    <AdjustAccountsModal
      v-model="showAdjust"
      :access="adjustAccess"
      @saved="onAdjusted"
      @error="showErrorSnackbar"
    />

    <ChangeAccessPlanModal
      v-model="showChangePlan"
      :access="changePlanAccess"
      @saved="onPlanChanged"
      @error="showErrorSnackbar"
    />

    <v-dialog v-model="showDeleteDialog" max-width="440px" :persistent="actionLoading">
      <v-card>
        <v-card-title class="headline">Eliminar asociación</v-card-title>
        <v-card-text>
          Esta acción elimina definitivamente la asociación con
          <strong>{{ deleteAccess && deleteAccess.companyName }}</strong>
          y no se puede deshacer. ¿Continuar?
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn text @click="showDeleteDialog = false" :disabled="actionLoading">Cancelar</v-btn>
          <v-btn
            v-if="canManageAccess"
            color="error"
            :loading="actionLoading"
            @click="doDelete"
          >Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="showSnackbar" :timeout="5000" :color="snackbarColor">
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="showSnackbar = false">Entendido</v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import CompaniesService from '../services/companie.service.js';
import AssociateCompanyModal from './AssociateCompanyModal.vue';
import AdjustAccountsModal from './AdjustAccountsModal.vue';
import ChangeAccessPlanModal from './ChangeAccessPlanModal.vue';
import { utilsMixin } from '@/mixins/utilsMixin';
import { can } from '@/utils/permissions';

export default {
  name: 'UserCompaniesMaintainer',
  mixins: [utilsMixin],
  components: { AssociateCompanyModal, AdjustAccountsModal, ChangeAccessPlanModal },
  props: {
    userId: { type: String, required: true },
    // Dentro de un diálogo: sin alto fijo, sombra ni margen de tarjeta.
    embedded: { type: Boolean, default: false }
  },
  data() {
    return {
      accesses: [],
      loading: false,
      actionLoading: false,
      showAssociate: false,
      showAdjust: false,
      adjustAccess: null,
      showChangePlan: false,
      changePlanAccess: null,
      showDeleteDialog: false,
      deleteAccess: null,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: ''
    };
  },
  computed: {
    canManageAccess() {
      return can('users:manage-company-access');
    }
  },
  watch: {
    userId: {
      handler(id) { if (id) this.fetchAccesses(); },
      immediate: true
    }
  },
  methods: {
    // Renders the contracted-memberships total. A membershipLimit of 1000 is the
    // backend's "unlimited" sentinel (default, not a real multiplication result),
    // so it is shown as ∞. Otherwise shows the capacity as-is (including 0), with
    // a '-' fallback when the backend sends null/undefined.
    formatTotal(item) {
      if (item.membershipLimit === 1000) return '∞';
      return item.capacity == null ? '-' : item.capacity;
    },
    async fetchAccesses() {
      this.loading = true;
      const res = await CompaniesService.getUserCompanyAccesses(this.userId);
      if (res.status === 'ok' && Array.isArray(res.data)) {
        this.accesses = res.data;
        // La cabecera de la 360 muestra los contratos: se le avisa tras cada carga o cambio.
        this.$emit('loaded', res.data);
      } else {
        // Preserve the previously loaded list on a failed refresh; only warn.
        this.showErrorSnackbar('No se pudieron cargar las compañías del usuario.');
      }
      this.loading = false;
    },
    openAssociate() {
      this.showAssociate = true;
    },
    async onAssociated() {
      this.showSuccessSnackbar('Compañía asociada correctamente');
      await this.fetchAccesses();
    },
    openAdjust(item) {
      this.adjustAccess = item;
      this.showAdjust = true;
    },
    openChangePlan(item) {
      this.changePlanAccess = item;
      this.showChangePlan = true;
    },
    async onPlanChanged() {
      this.showSuccessSnackbar('Plan actualizado');
      await this.fetchAccesses();
    },
    async onAdjusted() {
      this.showSuccessSnackbar('Cuentas actualizadas');
      await this.fetchAccesses();
    },
    async toggleStatus(item) {
      this.actionLoading = true;
      const res = await CompaniesService.setAccessStatus(item.id, item.status !== 'active');
      this.actionLoading = false;
      if (res.status === 'ok') {
        this.showSuccessSnackbar(item.status === 'active' ? 'Asociación desactivada' : 'Asociación reactivada');
        await this.fetchAccesses();
      } else {
        this.showErrorSnackbar('No se pudo cambiar el estado.');
      }
    },
    confirmDelete(item) {
      this.deleteAccess = item;
      this.showDeleteDialog = true;
    },
    async doDelete() {
      this.actionLoading = true;
      const res = await CompaniesService.deleteAccess(this.deleteAccess.id);
      this.actionLoading = false;
      this.showDeleteDialog = false;
      if (res.status === 'ok') {
        this.showSuccessSnackbar('Asociación eliminada');
        await this.fetchAccesses();
      } else {
        const msg = res.message === 'HAS_ACTIVE_MEMBERSHIPS'
          ? 'No se puede eliminar: la asociación tiene membresías activas.'
          : 'No se pudo eliminar la asociación.';
        this.showErrorSnackbar(msg);
      }
    }
  }
};
</script>

<style scoped>
.bento-card { border-radius: 8px; font-size: 0.78rem; }
.embedded-card { font-size: 0.8125rem; }
.embedded-card >>> .v-card__text.card-content { padding: 0; }
.third-card { height: 276px; overflow: hidden; display: flex; flex-direction: column; }
.third-card >>> .v-card__text.card-content { display: flex; flex-direction: column; height: 100%; padding-bottom: 8px; }
.header-row { flex-shrink: 0; }
.table-scroll { overflow-y: auto; flex: 1; }
.section-title { font-weight: 600; color: #1b1d21; font-size: 0.9rem; }
.empty-state { font-size: 0.85rem; }
.accounts-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  font-size: 0.72rem;
  font-weight: 700;
  color: #fff;
}
.accounts-badge--active { background-color: #2e7d32; }
.accounts-badge--inactive { background-color: #9e9e9e; }
</style>
